import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Car, Building2, DollarSign, TrendingUp, Shield, Battery, Users, ArrowRight, CheckCircle, Target, Briefcase, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import LeadCaptureModal from '../components/LeadCaptureModal';

export default function Investor() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [showLeadModal, setShowLeadModal] = useState(false);

  const slides = [
    'Cover', 'Problem', 'Solution', 'Car Sharing', 'Technology', 
    'Market', 'Business Model', 'Financials', 'Team', 'EBC Compliance', 'Ask'
  ];

  const coverStats = [
    { value: "$0", label: "Capital Cost per Building", color: "text-primary" },
    { value: "7,500", label: "EV Car Sharing Fleet", color: "text-red-500" },
    { value: "$180K", label: "Car Share Revenue/Building", color: "text-red-500" },
    { value: "$520K", label: "Total Annual Profit", color: "text-cyan-400" }
  ];

  const problems = [
    { icon: Zap, title: "Grid Constraints", description: "Ontario condos face 2+ year delays for EV infrastructure due to grid upgrades. Peak demand charges make installations economically impossible.", stats: "2+ Years | $300/kW-year", color: "red" },
    { icon: DollarSign, title: "Capital Barriers", description: "$3.5M average cost per building for comprehensive EV + car sharing infrastructure. Condo boards lack capital reserves.", stats: "$3.5M/Building | 0% Adoption", color: "orange" },
    { icon: Globe, title: "Stranded Assets", description: "40,000 abandoned gas wells in Ontario represent $30B in stranded energy assets. Environmental liabilities cost $500M+ annually.", stats: "40,000 Wells | $30B Value", color: "yellow" },
    { icon: Car, title: "Car Sharing Gap", description: "Urban condos lack dedicated car sharing. Residents pay $800+/month for parking + ownership vs $200/month shared mobility.", stats: "$800 vs $200 | 75% Savings", color: "red" }
  ];

  const solutionTimeline = [
    { step: 1, title: "$1 Well Acquisition", description: "Acquire abandoned gas wells for $1 with deferred payment. 500 MMcf reserves provide $750K collateral per well.", color: "primary" },
    { step: 2, title: "On-Site Generation", description: "Deploy 500kW CHP using retrofitted Mazda engines ($150/unit). Tri-fuel: natural gas/biogas/propane.", color: "primary" },
    { step: 3, title: "Car Sharing Infrastructure", description: "15 BYD EVs per building with dedicated charging. V2G bi-directional charging turns fleet into grid asset.", color: "red" },
    { step: 4, title: "Virtual Power Plant", description: "7,500 EVs aggregated into dispatchable grid resource. $120K/year grid services revenue per building.", color: "red" },
    { step: 5, title: "Carbon-Negative Operations", description: "10% biogas blend + EV fleet = carbon-negative mobility. BC-LCFS credits generate $60K+ annual carbon revenue.", color: "primary" }
  ];

  const buildingEconomics = [
    { label: "System Cost (EVs + Charging)", value: "$3.5M", type: "cost" },
    { label: "Gas Reserve Collateral", value: "-$1.5M", type: "credit" },
    { label: "Ontario Rebates", value: "-$1.4M", type: "credit" },
    { label: "Federal Tax Credits", value: "-$0.6M", type: "credit" }
  ];

  const revenueStreams = [
    { icon: Zap, title: "Electricity", value: "$280K", desc: "On-site gen savings", color: "primary" },
    { icon: Car, title: "Car Sharing", value: "$180K", desc: "15 EVs @ 60% util", color: "red" },
    { icon: Battery, title: "V2G Grid", value: "$120K", desc: "Fleet grid services", color: "cyan" },
    { icon: DollarSign, title: "GA Avoid", value: "$80K", desc: "Peak demand reduction", color: "yellow" },
    { icon: Globe, title: "Carbon", value: "$60K", desc: "BC-LCFS + CFR", color: "green" },
    { icon: Users, title: "Corporate", value: "$40K", desc: "Employee mobility", color: "purple" }
  ];

  const v2gRevenue = [
    { label: "Peak Shaving", value: "$45K" },
    { label: "Frequency Regulation", value: "$35K" },
    { label: "Demand Response", value: "$25K" },
    { label: "Energy Arbitrage", value: "$15K" }
  ];

  const fleetComposition = [
    { model: "BYD Dolphin (Compact)", units: 8, color: "bg-blue-400" },
    { model: "BYD Atto 3 (SUV)", units: 4, color: "bg-green-400" },
    { model: "BYD M3 (Van)", units: 2, color: "bg-yellow-400" },
    { model: "BYD Han (Premium)", units: 1, color: "bg-red-400" }
  ];

  const vppStats = [
    { value: "75 MW", label: "Dispatchable Capacity", detail: "500 bldgs × 150 kW" },
    { value: "300 MWh", label: "Storage Capacity", detail: "4 hr discharge duration" },
    { value: "<100ms", label: "Response Time", detail: "vs 5-10 min peaker plants" },
    { value: "$0", label: "Marginal Cost", detail: "vs $200-400/MWh gas plants" }
  ];

  const financialProjections = [
    { year: "Year 1", buildings: "50", evs: "750", profit: "$26M", carShare: "$13.5M", width: "10%" },
    { year: "Year 2", buildings: "200", evs: "3,000", profit: "$104M", carShare: "$54M", width: "40%" },
    { year: "Year 3", buildings: "500", evs: "7,500", profit: "$260M", carShare: "$135M", width: "100%" }
  ];

  const useOfFunds = [
    { label: "BYD EV Fleet (15 × 50 buildings)", percent: 35, color: "bg-red-500" },
    { label: "Well Acquisition & Collateral", percent: 25, color: "bg-primary" },
    { label: "CHP Unit Production", percent: 20, color: "bg-cyan-500" },
    { label: "Charging Infrastructure", percent: 12, color: "bg-yellow-500" },
    { label: "Working Capital", percent: 8, color: "bg-purple-500" }
  ];

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="investor-page">
      
      {/* Navigation Dots */}
      <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-40 hidden lg:flex flex-col gap-3">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => document.getElementById(`slide-${index}`)?.scrollIntoView({ behavior: 'smooth' })}
            className={`w-3 h-3 rounded-full transition-all ${activeSlide === index ? 'bg-primary scale-125 shadow-lg shadow-primary/50' : 'bg-white/20 hover:bg-white/40'}`}
            title={slide}
          />
        ))}
      </div>

      {/* Slide 0: Cover */}
      <section id="slide-0" className="min-h-screen flex items-center py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="flex gap-2 flex-wrap">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium text-primary">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                  EBC Qualified Investment
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-500/30 text-sm font-medium text-red-400">
                  <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse"></span>
                  BYD Partnership
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-black leading-tight">
                The <span className="text-primary">$1 Well</span><br/>
                <span className="bg-gradient-to-r from-red-500 to-primary bg-clip-text text-transparent">Car Sharing</span><br/>
                Revolution
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-lg">
                Zero-capital EV charging + condo-based car sharing fleet. 
                7,500 VPP-enabled EVs generating $260M annual profit while decarbonizing 5,000+ buildings.
              </p>
              
              <div className="flex gap-4 pt-4">
                <button onClick={() => setShowLeadModal(true)} className="px-8 py-4 bg-primary text-black font-bold hover:bg-primary/90 transition-all" data-testid="download-pitch-deck-btn">
                  Download Pitch Deck
                </button>
                <Link to="/contact" className="px-8 py-4 glass hover:bg-white/10 font-semibold transition-all">
                  Schedule Demo
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-cyan-500/20 to-red-500/20 rounded-3xl blur-3xl"></div>
              <div className="relative glass rounded-3xl p-8 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {coverStats.map((stat, index) => (
                    <div key={index} className={`${index % 2 === 1 ? 'bg-red-500/10 border-red-500/30' : 'bg-primary/10 border-primary/30'} border rounded-2xl p-6`}>
                      <div className={`text-4xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
                
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                      <Car className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold">BYD Fleet Partnership</div>
                      <div className="text-xs text-muted-foreground">15 EVs per building • Bi-directional V2G</div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-red-400">VPP Ready</div>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-cyan-400 flex items-center justify-center font-bold text-black">G</div>
                    <div>
                      <div className="font-semibold">GreenSmart Ltd.</div>
                      <div className="text-xs text-muted-foreground">Ontario Division</div>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-xs font-bold text-blue-400">
                    EBC COMPLIANT
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Slide 1: The Problem */}
      <section id="slide-1" className="min-h-screen flex items-center py-16 px-6 md:px-12 bg-accent/20">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-5xl font-bold mb-4">The <span className="text-red-500">Crisis</span></h2>
            <p className="text-xl text-muted-foreground">Four converging market failures creating a $6B+ opportunity</p>
          </motion.div>
          
          <div className="grid lg:grid-cols-4 gap-6 mb-8">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass rounded-2xl p-6 hover:bg-white/5 transition-all group"
              >
                <div className={`w-16 h-16 rounded-2xl bg-${problem.color}-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <problem.icon className={`w-8 h-8 text-${problem.color}-500`} />
                </div>
                <h3 className="text-xl font-bold mb-4">{problem.title}</h3>
                <p className="text-muted-foreground mb-4 text-sm">{problem.description}</p>
                <div className={`text-${problem.color}-400 font-semibold text-sm`}>{problem.stats}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-6 border-l-4 border-primary">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">The Regulatory Arbitrage</h4>
                  <p className="text-muted-foreground">O.Reg 116/01 exemption: CHP systems under 5MW for hot water require <span className="text-primary font-bold">NO environmental assessment</span> (30 days vs 2+ years).</p>
                </div>
              </div>
            </div>
            
            <div className="glass rounded-2xl p-6 border-l-4 border-red-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <Car className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2 text-red-400">The Mobility Opportunity</h4>
                  <p className="text-muted-foreground">Condo-based car sharing with <span className="text-red-400 font-bold">dedicated parking + charging</span> eliminates range anxiety. V2G-enabled fleet generates $120K/year grid revenue per building.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 2: The Solution */}
      <section id="slide-2" className="min-h-screen flex items-center py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-5xl font-bold mb-4">The <span className="text-primary">Solution</span></h2>
            <p className="text-xl text-muted-foreground">Zero-capital integrated energy + mobility platform</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {solutionTimeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative pl-8 border-l-2 ${item.color === 'red' ? 'border-red-500/30' : 'border-primary/30'} pb-8`}
                >
                  <div className={`absolute left-[-6px] top-0 w-3 h-3 rounded-full ${item.color === 'red' ? 'bg-red-500 shadow-lg shadow-red-500/50' : 'bg-primary shadow-lg shadow-primary/50'}`}></div>
                  <h3 className={`text-xl font-bold ${item.color === 'red' ? 'text-red-400' : 'text-primary'} mb-2`}>{item.step}. {item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="relative space-y-6">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-cyan-500/20 to-red-500/20 rounded-3xl blur-3xl"></div>
              
              <div className="relative glass rounded-3xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">Building Economics</h3>
                <div className="space-y-4">
                  {buildingEconomics.map((item, index) => (
                    <div key={index} className={`flex justify-between items-center p-4 ${item.type === 'credit' ? 'bg-primary/10 rounded-lg border border-primary/30' : 'bg-white/5 rounded-lg'}`}>
                      <span className={item.type === 'credit' ? 'text-primary' : 'text-muted-foreground'}>{item.label}</span>
                      <span className={`text-2xl font-bold ${item.type === 'credit' ? 'text-primary' : ''}`}>{item.value}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/20 pt-4 flex justify-between items-center">
                    <span className="text-xl font-bold">Net Capital Cost</span>
                    <span className="text-3xl font-bold text-primary">$0</span>
                  </div>
                </div>
              </div>

              <div className="relative bg-red-500/10 border border-red-500/30 rounded-3xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-center text-red-400">Car Sharing Revenue Model</h3>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">15</div>
                    <div className="text-xs text-muted-foreground">EVs per Building</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-1">60%</div>
                    <div className="text-xs text-muted-foreground">Utilization Target</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-400 mb-1">$12</div>
                    <div className="text-xs text-muted-foreground">Avg/Hour Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-400 mb-1">$180K</div>
                    <div className="text-xs text-muted-foreground">Annual Revenue</div>
                  </div>
                </div>
                <div className="bg-black/30 rounded-lg p-4 text-center">
                  <div className="text-sm text-muted-foreground mb-1">vs. Traditional Ownership</div>
                  <div className="text-2xl font-bold text-primary">75% Cost Savings for Residents</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 3: Car Sharing Platform */}
      <section id="slide-3" className="min-h-screen flex items-center py-16 px-6 md:px-12 bg-accent/20">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-5xl font-bold mb-4">Condo <span className="bg-gradient-to-r from-red-500 to-primary bg-clip-text text-transparent">Car Sharing</span></h2>
            <p className="text-xl text-muted-foreground">Resident-owned mobility fleet with V2G grid integration</p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2 space-y-6">
              <div className="glass rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400">
                    <Car className="w-6 h-6" />
                  </span>
                  The "GreenSmart Fleet" Model
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-bold text-primary mb-3">Resident Benefits</h4>
                    <ul className="space-y-3 text-muted-foreground">
                      {["Book instantly via mobile app", "Dedicated parking + guaranteed charging", "$200/month vs $800+ ownership cost", "Multiple vehicle types (sedan, SUV, van)"].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="space-y-4">
                    <h4 className="font-bold text-red-400 mb-3">Building Revenue</h4>
                    <ul className="space-y-3 text-muted-foreground">
                      {["$180K annual car share revenue", "$120K V2G grid services income", "60% utilization = 8.6 hrs/day/vehicle", "Carbon credits for shared mobility"].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <DollarSign className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { value: "500", label: "Buildings", detail: "7,500 EV Fleet" },
                  { value: "4,500", label: "Daily Trips", detail: "9 trips/building" },
                  { value: "$90M", label: "Annual Revenue", detail: "Car sharing only" }
                ].map((stat, i) => (
                  <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
                    <div className="text-4xl font-bold text-red-400 mb-2">{stat.value}</div>
                    <div className="text-sm text-muted-foreground mb-1">{stat.label}</div>
                    <div className="text-xs text-primary">{stat.detail}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="glass rounded-2xl p-6">
                <h4 className="font-bold text-lg mb-4 text-center">V2G Revenue Stack</h4>
                <div className="space-y-4">
                  {v2gRevenue.map((item, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                      <span className="font-bold text-cyan-400">{item.value}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/20 pt-3 flex justify-between items-center">
                    <span className="font-bold">Total/Building</span>
                    <span className="text-xl font-bold text-red-400">$120K</span>
                  </div>
                </div>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6">
                <h4 className="font-bold text-lg mb-4 text-center">Fleet Composition</h4>
                <div className="space-y-3">
                  {fleetComposition.map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                        <span className="text-sm">{item.model}</span>
                      </div>
                      <span className="text-sm font-bold">{item.units} units</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 text-center">
                  <div className="text-xs text-muted-foreground">All vehicles V2G-enabled via</div>
                  <div className="text-lg font-bold text-red-400">BYD Blade Battery</div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass rounded-2xl p-8 border border-red-500/30">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h3 className="text-2xl font-bold">Competitive Advantage: Condo-Based vs Traditional Car Share</h3>
              <span className="px-3 py-1 rounded-full bg-red-500/20 text-sm font-bold text-red-400">GreenSmart Advantage</span>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { value: "0 min", label: "Walk to Vehicle", detail: "vs 10-15 min street parking" },
                { value: "100%", label: "Charged Guarantee", detail: "vs 30% uncharged fleet" },
                { value: "24/7", label: "Building Security", detail: "vs street vandalism risk" },
                { value: "V2G", label: "Revenue Generation", detail: "vs pure cost center" }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-bold text-red-400 mb-2">{item.value}</div>
                  <div className="text-sm text-muted-foreground">{item.label}</div>
                  <div className="text-xs text-muted-foreground/60 mt-1">{item.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Slide 4: Technology */}
      <section id="slide-4" className="min-h-screen flex items-center py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-5xl font-bold mb-4">Technology <span className="text-primary">Stack</span></h2>
            <p className="text-xl text-muted-foreground">Integrated energy + mobility + grid services platform</p>
          </motion.div>

          <div className="glass rounded-2xl p-8 border border-primary/30">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h3 className="text-2xl font-bold">The VPP Advantage: 7,500 EVs as Grid Infrastructure</h3>
              <span className="text-primary font-mono">vs. Traditional Power Plants</span>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              {vppStats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                  <div className="text-xs text-muted-foreground/60 mt-1">{stat.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Slide 5: Business Model */}
      <section id="slide-5" className="min-h-screen flex items-center py-16 px-6 md:px-12 bg-accent/20">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-5xl font-bold mb-4">Revenue <span className="text-primary">Model</span></h2>
            <p className="text-xl text-muted-foreground">Six interconnected streams: mobility, energy, and grid services</p>
          </motion.div>

          <div className="grid lg:grid-cols-6 gap-4 mb-8">
            {revenueStreams.map((stream, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`glass rounded-2xl p-4 text-center group hover:bg-white/5 transition-all ${stream.color === 'red' ? 'border border-red-500/30' : ''}`}
              >
                <div className={`w-12 h-12 rounded-full bg-${stream.color}-500/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                  <stream.icon className={`w-6 h-6 text-${stream.color}-400`} />
                </div>
                <h4 className="font-bold text-sm mb-1">{stream.title}</h4>
                <div className={`text-xl font-bold text-${stream.color}-400`}>{stream.value}</div>
                <div className="text-xs text-muted-foreground">{stream.desc}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Unit Economics (Per Building/Year)</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total Revenue</span>
                  <span className="text-2xl font-bold">$760K</span>
                </div>
                <div className="flex justify-between items-center text-red-400">
                  <span className="text-muted-foreground">Car Share Revenue (24%)</span>
                  <span className="text-xl">$180K</span>
                </div>
                <div className="flex justify-between items-center text-cyan-400">
                  <span className="text-muted-foreground">V2G Grid Revenue (16%)</span>
                  <span className="text-xl">$120K</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Operating Costs</span>
                  <span className="text-xl">-$240K</span>
                </div>
                <div className="border-t border-white/20 pt-4 flex justify-between items-center">
                  <span className="text-lg font-bold">Net Profit</span>
                  <span className="text-3xl font-bold text-primary">$520K</span>
                </div>
              </div>
            </div>

            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Scaling Trajectory: Car Sharing Fleet</h3>
              <div className="space-y-6">
                {financialProjections.map((proj, i) => (
                  <div key={i}>
                    <div className="flex justify-between mb-2">
                      <span className="font-semibold">{proj.year}</span>
                      <span className="text-red-400">{proj.buildings} Buildings / {proj.evs} EVs</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-gradient-to-r from-red-500 to-primary h-2 rounded-full" style={{ width: proj.width }}></div>
                    </div>
                    <div className="text-right text-sm text-muted-foreground mt-1">{proj.profit} Profit | {proj.carShare} Car Share</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 6: Investment Ask */}
      <section id="slide-6" className="min-h-screen flex items-center py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl font-bold mb-6">Investment <span className="text-primary">Opportunity</span></h2>
              <p className="text-xl text-muted-foreground mb-8">Join the $1 Well Car Sharing Revolution. Zero-capital deployment with immediate cash flow from mobility services.</p>
              
              <div className="space-y-6 mb-8">
                {[
                  { icon: CheckCircle, title: "Proven Collateral", desc: "$750K gas + $450K EV fleet per building", color: "primary" },
                  { icon: Car, title: "Immediate Utilization", desc: "Car sharing revenue Day 1, 60% target utilization", color: "red" },
                  { icon: Zap, title: "V2G Revenue Stream", desc: "$120K/year grid services per building", color: "cyan" },
                  { icon: Users, title: "Multiple Exit Paths", desc: "Clean Energy REIT IPO, strategic sale, or cash flow", color: "purple" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/20 flex items-center justify-center`}>
                      <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                    </div>
                    <div>
                      <h4 className="font-bold">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Link to="/contact" className="px-8 py-4 bg-primary text-black font-bold hover:bg-primary/90 transition-all flex items-center gap-2">
                  Request Investor Package <ArrowRight size={20} />
                </Link>
                <Link to="/contact" className="px-8 py-4 glass hover:bg-white/10 font-semibold transition-all">
                  Schedule Fleet Demo
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="glass rounded-2xl p-8 border border-primary/30">
                <h3 className="text-2xl font-bold mb-6 text-center">Use of Funds</h3>
                <div className="space-y-4">
                  {useOfFunds.map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between mb-2">
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-bold">{item.percent}%</span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div className={`${item.color} h-2 rounded-full`} style={{ width: `${item.percent}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-red-400 mb-1">750</div>
                  <div className="text-sm text-muted-foreground">Initial EV Fleet</div>
                  <div className="text-xs text-primary mt-1">50 buildings</div>
                </div>
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-primary mb-1">Q2 2025</div>
                  <div className="text-sm text-muted-foreground">First Deployments</div>
                  <div className="text-xs text-primary mt-1">Toronto launch</div>
                </div>
              </div>

              <div className="glass rounded-xl p-6 text-center">
                <p className="text-muted-foreground text-sm mb-2">Contact</p>
                <p className="text-xl font-bold">info@greensmart.ltd</p>
                <p className="text-primary font-semibold mt-1">(778) 729-2202</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 px-6 md:px-12 bg-gradient-to-r from-primary/20 to-red-500/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to <span className="text-primary">Invest</span>?</h2>
          <p className="text-xl text-muted-foreground mb-8">Contact our team for the full investor package and fleet demonstration.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-primary text-black font-bold hover:bg-primary/90 transition-all">
              Contact Us Today
            </Link>
            <a href="tel:+17787292202" className="px-8 py-4 border border-primary text-primary font-bold hover:bg-primary/10 transition-all">
              Call (778) 729-2202
            </a>
          </div>
        </div>
      </section>

      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        deckType="investor"
        downloadUrl={`${process.env.REACT_APP_BACKEND_URL}/api/investor-deck/download`}
      />
    </div>
  );
}
